import firebase from 'firebase/compat/app'
import store from 'src/rematch'
import packageJson from '../../package.json'

/**
 * Detects loading spinners that never resolve (mostly seen on mobile after
 * resuming a tab), reports diagnostics remotely and recovers where it's safe.
 * Reports are kept in localStorage until sent, as the network or auth may be
 * exactly what's broken at the time of the stall.
 */

export const WATCHDOG_TIMEOUT_MS = 20000

const PENDING_KEY = 'loadingWatchdogPending'
const RELOAD_KEY = 'loadingWatchdogReloadedAt'
const MAX_PENDING = 5
const AUTO_RELOAD_COOLDOWN_MS = 5 * 60 * 1000

// spinners shown before the app is usable, safe to auto-reload
// ('overlay' may also cover long-running actions like bulk booking)
const BOOT_SOURCES = ['persist', 'auth', 'settings']

const reportedSources = new Set()

const readPending = () => {
    try {
        return JSON.parse(localStorage.getItem(PENDING_KEY)) ?? []
    } catch {
        return []
    }
}

const writePending = reports => {
    try {
        if (reports.length) {
            localStorage.setItem(PENDING_KEY, JSON.stringify(reports.slice(-MAX_PENDING)))
        } else {
            localStorage.removeItem(PENDING_KEY)
        }
    } catch {
        // storage unavailable (private mode, quota), nothing to do
    }
}

const pendingEffects = loadingState =>
    Object.entries(loadingState?.effects ?? {}).flatMap(([model, effects]) =>
        Object.entries(effects)
            .filter(([, isLoading]) => isLoading)
            .map(([effect]) => `${model}.${effect}`)
    )

const collectDiagnostics = source => {
    const state = store.getState()
    const { auth = {}, profile = {} } = state.firebase ?? {}
    const requesting = state.firestore?.status?.requesting ?? {}
    const selectedDate = state.slotPicker?.selectedDate

    let hasCurrentUser = null
    try {
        hasCurrentUser = !!firebase.auth().currentUser
    } catch {
        // firebase not initialised
    }

    return {
        source,
        reportedAt: new Date().toISOString(),
        path: window.location.pathname,
        appVersion: packageJson.version,
        secondsSinceLoad: Math.round(performance.now() / 1000),
        navigationType: performance.getEntriesByType?.('navigation')?.[0]?.type ?? null,
        online: navigator.onLine,
        visibility: document.visibilityState,
        userAgent: navigator.userAgent,
        auth: {
            isLoaded: auth.isLoaded,
            isEmpty: auth.isEmpty,
            emailVerified: auth.emailVerified ?? null,
            hasCurrentUser
        },
        profile: {
            isLoaded: profile.isLoaded,
            hasToken: !!profile.token,
            phoneExists: !!profile.token?.claims?.phoneExists
        },
        selectedDate: selectedDate ? String(selectedDate) : null,
        now: state.utils?.now ? String(state.utils.now) : null,
        areSlotsLoading: state.slotPicker?.areSlotsLoading ?? null,
        batchRunning: state.slotPicker?.batchRunning ?? null,
        utilsLoading: state.utils?.loading ?? null,
        logoutStarted: state.utils?.logoutStarted ?? null,
        pendingListeners: Object.keys(requesting).filter(key => requesting[key]),
        pendingEffects: pendingEffects(state.loading)
    }
}

const sendReport = async report => {
    await import('firebase/compat/functions')
    // noinspection JSUnresolvedFunction
    const log = firebase.functions().httpsCallable('functions-callableLog')
    await log({
        type: 'warn',
        message: `Loading watchdog: spinner stalled (${report.source})`,
        diagnostics: report
    })
}

export const flushPendingWatchdogReports = async () => {
    const pending = readPending()
    if (!pending.length || !firebase.apps.length || !firebase.auth().currentUser) {
        return
    }

    const failed = []
    for (const report of pending) {
        try {
            await sendReport(report)
        } catch {
            failed.push(report)
        }
    }
    writePending(failed)
}

const canAutoReload = () => {
    try {
        const lastReload = parseInt(sessionStorage.getItem(RELOAD_KEY), 10)
        return !lastReload || Date.now() - lastReload > AUTO_RELOAD_COOLDOWN_MS
    } catch {
        return false
    }
}

/**
 * @param source which spinner stalled: 'persist', 'auth', 'settings' or 'overlay'
 */
export const handleStalledSpinner = source => {
    if (reportedSources.has(source)) {
        return
    }
    reportedSources.add(source)

    const autoReload = BOOT_SOURCES.includes(source) && canAutoReload()
    const report = { ...collectDiagnostics(source), autoReload }

    writePending([...readPending(), report])

    if (autoReload) {
        try {
            sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
        } catch {
            // ignore
        }
        // report is sent on the next load
        window.location.reload()
        return
    }

    flushPendingWatchdogReports().catch(() => {})
}
